import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Search, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Account, Contact } from "@shared/schema";

interface FacilityParty {
  id: string;
  facilityId: string;
  partyType: "account" | "contact";
  accountId?: string | null;
  contactId?: string | null;
  partyRole: string;
  ownershipPercentage?: string | null;
  isPrimary?: boolean | null;
  status?: string | null;
  notes?: string | null;
}

interface AddFacilityPartyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityId: string;
  clientGroupId?: string;
  dealId?: string; // ISSUE-149 REWORK #3: Pass dealId for exact cache invalidation
  party?: FacilityParty | null; // Optional party for edit mode
  onPartyAdded?: () => void; // Callback to trigger parent refresh
}

const FACILITY_PARTY_ROLES = [
  { value: "borrower", label: "Borrower" },
  { value: "guarantor", label: "Guarantor" },
  { value: "authorised_signatory", label: "Authorised Signatory" },
];

export function AddFacilityPartyModal({
  open,
  onOpenChange,
  facilityId,
  clientGroupId,
  dealId, // ISSUE-149 REWORK #3: Destructure dealId from props
  party,
  onPartyAdded,
}: AddFacilityPartyModalProps) {
  const isEditMode = !!party;
  const [partyType, setPartyType] = useState<"account" | "contact">("account");
  const [selectedPartyId, setSelectedPartyId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<string>("borrower");
  const [isPrimary, setIsPrimary] = useState<boolean>(false);
  const [ownershipPercentage, setOwnershipPercentage] = useState<string>("");
  const [ownershipError, setOwnershipError] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    legalName: "",
    abn: "",
  });
  const [abnError, setAbnError] = useState<string>("");
  const { toast} = useToast();

  // Pre-populate form when party prop changes (ISSUE-138 fix - same as ISSUE-136)
  useEffect(() => {
    if (party && open) {
      // Edit mode: populate from party prop
      setPartyType(party.partyType);
      setSelectedPartyId(party.partyType === "account" ? (party.accountId || "") : (party.contactId || ""));
      setSelectedRole(party.partyRole);
      setIsPrimary(party.isPrimary || false);
      setOwnershipPercentage(party.ownershipPercentage != null ? String(party.ownershipPercentage) : "");
      setOwnershipError("");
    } else if (!open) {
      // Reset when modal closes
      setPartyType("account");
      setSelectedPartyId("");
      setSelectedRole("borrower");
      setIsPrimary(false);
      setOwnershipPercentage("");
      setOwnershipError("");
      setSearchTerm("");
      setShowQuickAdd(false);
      setQuickAddData({ firstName: "", lastName: "", email: "", legalName: "", abn: "" });
      setAbnError("");
    }
  }, [party, open]);

  // Fetch accounts (filtered by client group)
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: open && partyType === "account",
  });

  // Fetch contacts (filtered by client group)
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { ...(clientGroupId ? { clientGroupId } : {}) }],
    enabled: open && partyType === "contact",
  });

  // Fetch existing facility parties for validation
  const { data: existingParties = [] } = useQuery<FacilityParty[]>({
    queryKey: [`/api/crm/facilities/${facilityId}/parties`],
    enabled: open && !!facilityId,
  });

  const quickAddMutation = useMutation({
    mutationFn: async () => {
      if (partyType === "contact") {
        const response = await apiRequest("POST", "/api/crm/contacts", {
          clientGroupId,
          firstName: quickAddData.firstName,
          lastName: quickAddData.lastName,
          email: quickAddData.email,
          contactType: "individual",
        });
        return response.json() as Promise<Contact>;
      } else {
        const response = await apiRequest("POST", "/api/crm/accounts", {
          clientGroupId,
          legalName: quickAddData.legalName,
          abn: quickAddData.abn,
          entityType: "company",
        });
        return response.json() as Promise<Account>;
      }
    },
    onSuccess: async (newEntity: Contact | Account) => {
      // Refetch queries and wait for completion (fixes race condition)
      await queryClient.refetchQueries({
        queryKey: ["/api/crm/contacts", clientGroupId]
      });
      await queryClient.refetchQueries({
        queryKey: ["/api/crm/accounts", clientGroupId]
      });

      // Auto-select the newly created entity (ISSUE-129 fix)
      setSelectedPartyId(newEntity.id);

      toast({
        title: `${partyType === "contact" ? "Contact" : "Account"} created`,
        description: `The ${partyType} has been successfully created and selected.`,
      });

      setShowQuickAdd(false);
      setQuickAddData({ firstName: "", lastName: "", email: "", legalName: "", abn: "" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || `Failed to create ${partyType}`,
        variant: "destructive",
      });
    },
  });

  const addPartyMutation = useMutation({
    mutationFn: async () => {
      if (isEditMode && party) {
        // EDIT MODE: Update existing party
        const payload: {
          partyRole: string;
          isPrimary?: boolean | null;
          ownershipPercentage?: string | null;
        } = {
          partyRole: selectedRole,
          isPrimary: isPrimary,
          ownershipPercentage: ownershipPercentage.trim() || null,
        };

        await apiRequest("PATCH", `/api/crm/facilities/${facilityId}/parties/${party.id}`, payload);
      } else {
        // CREATE MODE: Add new party
        const payload: {
          facilityId: string;
          partyType: "account" | "contact";
          accountId?: string;
          contactId?: string;
          partyRole: string;
          isPrimary?: boolean | null;
          ownershipPercentage?: string | null;
        } = {
          facilityId,
          partyType,
          partyRole: selectedRole,
          isPrimary: isPrimary,
          ownershipPercentage: ownershipPercentage.trim() || null,
        };

        // Set accountId or contactId based on partyType
        if (partyType === "account") {
          payload.accountId = selectedPartyId;
        } else {
          payload.contactId = selectedPartyId;
        }

        await apiRequest("POST", `/api/crm/facilities/${facilityId}/parties`, payload);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/facilities/${facilityId}/parties`] });
      // ISSUE-149 REWORK #3: Use exact query key (Investigation Agent finding)
      // Exact key works because it matches deal-parties-panel.tsx line 68 format
      // Wildcard pattern failed due to staleTime: Infinity preventing refetch of inactive queries
      if (dealId) {
        queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/parties`] });
      }
      toast({
        title: isEditMode ? "Party updated" : "Party added",
        description: isEditMode
          ? "The party has been successfully updated."
          : "The party has been successfully added to this facility.",
      });

      // Trigger parent refresh callback
      if (onPartyAdded) {
        onPartyAdded();
      }

      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add party",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    if (!isEditMode) {
      setPartyType("account");
      setSelectedPartyId("");
      setSelectedRole("borrower");
      setIsPrimary(false);
      setOwnershipPercentage("");
      setOwnershipError("");
      setSearchTerm("");
      setShowQuickAdd(false);
      setQuickAddData({ firstName: "", lastName: "", email: "", legalName: "", abn: "" });
      setAbnError("");
    }
  };

  const validateABN = (abn: string): string => {
    // Remove spaces and validate length
    const cleanABN = abn.replace(/\s/g, "");
    if (cleanABN.length === 0) return ""; // Allow empty (will be caught by required validation)
    if (cleanABN.length > 11) return "ABN cannot exceed 11 digits";
    if (cleanABN.length > 0 && cleanABN.length < 11) return "ABN must be exactly 11 digits";
    if (!/^\d+$/.test(cleanABN)) return "ABN must contain only digits";
    return ""; // Valid
  };

  const handleABNChange = (value: string) => {
    setQuickAddData({ ...quickAddData, abn: value });
    const error = validateABN(value);
    setAbnError(error);
  };

  // Validate role conflicts (ISSUE-139)
  const validateRoleConflict = (): boolean => {
    // In edit mode, allow same role for same party (no conflict with self)
    const partiesToCheck = isEditMode && party
      ? existingParties.filter(p => p.id !== party.id)
      : existingParties;

    // Determine the partyId for comparison
    const currentPartyId = selectedPartyId;

    // Find if this party already exists with a conflicting role
    const conflictingParty = partiesToCheck.find((existingParty) => {
      // Match by accountId or contactId depending on party type
      const matchesParty = partyType === "account"
        ? existingParty.accountId === currentPartyId
        : existingParty.contactId === currentPartyId;

      // Check for role conflict (Borrower <-> Guarantor)
      const hasConflict =
        (selectedRole === "borrower" && existingParty.partyRole === "guarantor") ||
        (selectedRole === "guarantor" && existingParty.partyRole === "borrower");

      return matchesParty && hasConflict;
    });

    if (conflictingParty) {
      const existingRoleLabel = conflictingParty.partyRole === "borrower" ? "Borrower" : "Guarantor";

      toast({
        title: "Role conflict",
        description: `This party is already a ${existingRoleLabel} on this facility. A party cannot be both Borrower and Guarantor.`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleAdd = () => {
    // Skip selectedPartyId validation in edit mode (party already exists)
    if (!isEditMode && !selectedPartyId) {
      toast({
        title: "No party selected",
        description: "Please select a party to add to this facility.",
        variant: "destructive",
      });
      return;
    }

    // Validate ownership percentage range (NaN/Infinity must also be rejected)
    if (ownershipPercentage) {
      const n = parseFloat(ownershipPercentage);
      if (!isFinite(n) || n < 0 || n > 100) {
        setOwnershipError("Must be between 0 and 100");
        return;
      }
    }

    // Validate role conflict before submitting
    if (!validateRoleConflict()) {
      return;
    }

    addPartyMutation.mutate();
  };

  const handleQuickAdd = () => {
    if (partyType === "contact") {
      if (!quickAddData.firstName || !quickAddData.lastName || !quickAddData.email) {
        toast({
          title: "Missing fields",
          description: "Please fill in first name, last name, and email.",
          variant: "destructive",
        });
        return;
      }
    } else {
      if (!quickAddData.legalName || !quickAddData.abn) {
        toast({
          title: "Missing fields",
          description: "Please fill in legal name and ABN.",
          variant: "destructive",
        });
        return;
      }
    }
    quickAddMutation.mutate();
  };

  // Filter parties by search term (null-safe per ISSUE-094 pattern)
  const filteredAccounts = accounts.filter((account) =>
    account.legalName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContacts = contacts.filter((contact) => {
    const fullName = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Party" : "Add Party to Facility"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the party's role, ownership percentage, and primary/secondary designation."
              : "Add a party to this facility. This will also add them to the deal via party propagation (BR-DS-012)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Party Type Selection - HIDE IN EDIT MODE */}
          {!isEditMode && (
            <>
              <div className="space-y-2">
                <Label>Party Type</Label>
                <RadioGroup value={partyType} onValueChange={(value: any) => {
                  setPartyType(value);
                  setSelectedPartyId("");
                }}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="account" id="account" />
                    <Label htmlFor="account" className="font-normal cursor-pointer">Account/Company</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="contact" id="contact" />
                    <Label htmlFor="contact" className="font-normal cursor-pointer">Contact/Individual</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Search */}
              <div className="space-y-2">
                <Label>Search {partyType === "account" ? "Accounts" : "Contacts"}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${partyType === "account" ? "accounts" : "contacts"}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              {/* Party Selection */}
              <div className="space-y-2">
                <Label>Select {partyType === "account" ? "Account" : "Contact"}</Label>
                <Select value={selectedPartyId} onValueChange={setSelectedPartyId}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${partyType}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {partyType === "account"
                      ? filteredAccounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.legalName}
                          </SelectItem>
                        ))
                      : filteredContacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.firstName} {contact.lastName}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Add Buttons */}
              <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPartyType("contact");
                setShowQuickAdd(!showQuickAdd);
              }}
              className="flex-1"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Contact
              {showQuickAdd && partyType === "contact" ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setPartyType("account");
                setShowQuickAdd(!showQuickAdd);
              }}
              className="flex-1"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add New Account
              {showQuickAdd && partyType === "account" ? (
                <ChevronUp className="ml-2 h-4 w-4" />
              ) : (
                <ChevronDown className="ml-2 h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Inline Quick Add Form */}
          {showQuickAdd && (
            <div className="rounded-md border border-border bg-muted/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">
                  Create New {partyType === "contact" ? "Contact" : "Account"}
                </h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowQuickAdd(false)}
                >
                  Cancel
                </Button>
              </div>
              {partyType === "contact" ? (
                <>
                  <div className="space-y-2">
                    <Label>First Name</Label>
                    <Input
                      placeholder="Enter first name"
                      value={quickAddData.firstName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, firstName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name</Label>
                    <Input
                      placeholder="Enter last name"
                      value={quickAddData.lastName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, lastName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="Enter email"
                      value={quickAddData.email}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, email: e.target.value })
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Legal Name</Label>
                    <Input
                      placeholder="Enter company legal name"
                      value={quickAddData.legalName}
                      onChange={(e) =>
                        setQuickAddData({ ...quickAddData, legalName: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ABN</Label>
                    <Input
                      placeholder="Enter ABN (11 digits)"
                      value={quickAddData.abn}
                      onChange={(e) => handleABNChange(e.target.value)}
                      className={abnError ? "border-destructive" : ""}
                    />
                    {abnError && (
                      <p className="text-xs text-destructive">{abnError}</p>
                    )}
                  </div>
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Client group inherited from facility context (locked)
              </p>
              <Button
                type="button"
                onClick={handleQuickAdd}
                disabled={quickAddMutation.isPending || (partyType === "account" && !!abnError)}
                className="w-full"
              >
                {quickAddMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create & Link
              </Button>
            </div>
          )}
            </>
          )}

          {/* Role Selection */}
          <div className="space-y-2">
            <Label>Party Role</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FACILITY_PARTY_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Ownership Percentage */}
          <div className="space-y-2">
            <Label htmlFor="ownershipPercentage">Ownership Percentage (%)</Label>
            <Input
              id="ownershipPercentage"
              type="number"
              min="0"
              max="100"
              step="0.01"
              placeholder="e.g. 25.00"
              value={ownershipPercentage}
              onChange={(e) => {
                const val = e.target.value;
                setOwnershipPercentage(val);
                const n = parseFloat(val);
                if (val && (!isFinite(n) || n < 0 || n > 100)) {
                  setOwnershipError("Must be between 0 and 100");
                } else {
                  setOwnershipError("");
                }
              }}
              className={ownershipError ? "border-destructive" : ""}
            />
            {ownershipError && (
              <p className="text-xs text-destructive">{ownershipError}</p>
            )}
          </div>

          {/* Is Primary Borrower */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPrimary"
              checked={isPrimary}
              onCheckedChange={(checked) => setIsPrimary(checked as boolean)}
            />
            <Label htmlFor="isPrimary" className="font-normal cursor-pointer">
              Primary Borrower/Party
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={addPartyMutation.isPending || (!isEditMode && !selectedPartyId) || !!ownershipError}
          >
            {addPartyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? "Update Party" : "Add Party"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
